import React, { ReactNode, useEffect, useState } from 'react';
import classnames from 'classnames';
import styles from './filterDropdown.module.scss';
import { BsSearch, BsChevronDown } from 'react-icons/bs';
import { SingleValue, StylesConfig } from 'react-select';
import AsyncSelect from 'react-select/async';
import _ from 'lodash';

export interface SelectOption<T> {
  readonly value: T;
  readonly label: string;
}

export interface FilterDropdownProps<T> {
  defaultValue: SelectOption<T> | null;
  defaultOptions: SelectOption<T>[] | boolean;
  placeHolder?: string;
  noOptionsMessage?: string;
  loadOptions: (
    inputValue: string,
    callback: (options: SelectOption<T>[]) => void
  ) => void;
  onChange?: (option: T | undefined) => void;

  /**
   * The dropdown will behave as a controlled component if a `value` is provided,
   * otherwise it'll behave as an uncontrolled field.
   */
  value?: SelectOption<T> | null;
}

function FilterDropdown<T>({
  defaultValue,
  defaultOptions,
  loadOptions,
  onChange,
  placeHolder = 'Select an Option',
  noOptionsMessage = 'No options',
  value: controlledValue,
}: FilterDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState<SelectOption<T> | null>(defaultValue);

  useEffect(() => {
    if (controlledValue !== undefined) setValue(controlledValue);
  }, [controlledValue]);

  const selectStyles: StylesConfig<SelectOption<T>, false> = {
    option: (provided, state) => ({
      ...provided,
      color: 'black',
      backgroundColor: state.isFocused ? '#DDDDDD' : 'white',
      borderBottom: '1px solid #DDDDDD',
      ':last-child': {
        borderBottom: '0px',
      },
      padding: 10,
      cursor: 'pointer',
      ':hover': {
        backgroundColor: '#DDDDDD',
      },
    }),
    container: () => ({
      width: '677px',
      backgroundColor: '#F8FAFF',
      border: '1px solid #d0d0d057',
      borderRadius: '5px 5px 0px 0px',
      boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
      position: 'absolute',
    }),
    control: () => ({
      background: ' #FFFFFF',
      border: ' 1.5px solid #DDE5F7',
      borderRadius: ' 50px',
      margin: ' 10px',
      display: ' flex',
      cursor: ' text',
      height: '50px',
      paddingRight: '20px',
    }),
    menuPortal: (base) => ({ ...base, zIndex: 50000 }),
    menu: (provided) => ({
      ...provided,
      marginTop: 0,
      boxSizing: 'border-box',
      borderRadius: '5px 5px 0px 0px',
      boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
    }),
    menuList: (base) => ({
      ...base,
      paddingTop: 0,
      paddingBottom: 0,
      boxSizing: 'border-box',
      maxHeight: '240px',
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: '#285FCB',
      ':hover': {
        color: '#285FCB',
      },
      position: 'absolute',
      left: '630px',
      bottom: '5px',
      cursor: 'pointer',
    }),
    placeholder: (base, { isDisabled }) => ({
      ...base,
      color: isDisabled ? base.color : '#666666',
    }),
    indicatorsContainer: (base) => ({
      ...base,
      alignSelf: 'center',
    }),
  };

  function handleChange(option: SingleValue<SelectOption<T>>) {
    setIsOpen(false);
    onChange?.(option?.value);
    setValue(option);
  }

  return (
    <Dropdown
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      target={
        <>
          <div
            onClick={() => setIsOpen((prev) => !prev)}
            className={classnames(styles.inputContainer)}
            role="button"
          >
            <span
              className={classnames(styles.wrapText, {
                [styles.placeholder]: !value,
              })}
            >
              {value ? value.label : placeHolder}
            </span>

            <div>
              <BsChevronDown style={{ fill: 'var(--primary)' }} />
            </div>
          </div>
        </>
      }
    >
      <AsyncSelect
        autoFocus
        aria-label="filter-dropdown"
        components={{ DropdownIndicator, IndicatorSeparator: null }}
        backspaceRemovesValue={false}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        tabSelectsValue={false}
        isClearable={false}
        menuIsOpen={true}
        cacheOptions
        styles={selectStyles}
        loadOptions={loadOptions}
        defaultOptions={defaultOptions}
        value={value}
        onChange={handleChange}
        noOptionsMessage={() => noOptionsMessage}
        isOptionSelected={(currentOption, options) =>
          options.some(({ value }) => _.isEqual(currentOption.value, value))
        }
      />
    </Dropdown>
  );
}

const Menu = (props: JSX.IntrinsicElements['div']) => {
  const shadow = 'hsla(218, 50%, 10%, 0.1)';
  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: 4,
        boxShadow: `0 0 0 1px ${shadow}, 0 4px 11px ${shadow}`,
        position: 'absolute',
        zIndex: 2,
      }}
      {...props}
    />
  );
};

const Blanket = (props: JSX.IntrinsicElements['div']) => (
  <div
    style={{
      bottom: 0,
      left: 0,
      top: 0,
      right: 0,
      position: 'fixed',
      zIndex: 1,
    }}
    {...props}
  />
);

const Dropdown = ({
  children,
  isOpen,
  target,
  onClose,
}: {
  children?: ReactNode;
  readonly isOpen: boolean;
  readonly target: ReactNode;
  readonly onClose: () => void;
}) => (
  <div style={{ position: 'relative' }} data-cy="dropdown">
    {target}
    {isOpen ? <Menu>{children}</Menu> : null}
    {isOpen ? <Blanket onClick={onClose} /> : null}
  </div>
);

const DropdownIndicator = () => (
  <BsSearch size={20} style={{ fill: '#285FCB' }} />
);

export default FilterDropdown;
